import React, { useState } from 'react';
import { createApiUrl } from '../../config/api';
import SimpleCaptcha from './SimpleCaptcha';
import './support.css';

const SupportLanding = () => {
  const [ticketForm, setTicketForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    subject: '',
    message: '',
    category: '',
    priority: 'Normal'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [submittedTicketId, setSubmittedTicketId] = useState(null);
  const [submittedTicketToken, setSubmittedTicketToken] = useState(null);

  // Help articles data (scraped from paramountlandscaping.ca)
  const helpArticles = [
    {
      id: 1,
      title: "Spring Landscaping Checklist: Get Your Yard Ready for the Season",
      category: "Seasonal Care",
      content: "Prepare your landscape for spring with our comprehensive checklist including lawn aeration, pruning, fertilizing, and planting new additions.",
      tags: ["Spring", "Lawn Care", "Maintenance"]
    },
    {
      id: 2,
      title: "Landscaping Trends 2025: Crafting Beautiful Spaces",
      category: "Design",
      content: "Discover the latest landscaping trends including sustainable practices, native plants, and modern outdoor living spaces.",
      tags: ["Trends", "Design", "Sustainability"]
    },
    {
      id: 3,
      title: "Top Yard Fungicides to Combat Common Lawn Diseases",
      category: "Lawn Care",
      content: "Learn about effective fungicides and treatments for common lawn diseases like brown patch, dollar spot, and rust.",
      tags: ["Disease", "Treatment", "Lawn Health"]
    },
    {
      id: 4,
      title: "Winter Lawn Protection: Keeping Your Grass Healthy",
      category: "Seasonal Care",
      content: "Essential tips for protecting your lawn during winter months including proper mowing, fertilizing, and snow management.",
      tags: ["Winter", "Protection", "Lawn Care"]
    },
    {
      id: 5,
      title: "Irrigation System Maintenance Guide",
      category: "Irrigation",
      content: "Complete guide to maintaining your irrigation system including winterization, spring startup, and regular maintenance tasks.",
      tags: ["Irrigation", "Maintenance", "Watering"]
    },
    {
      id: 6,
      title: "Tree and Shrub Pruning Best Practices",
      category: "Pruning",
      content: "Professional tips for proper tree and shrub pruning including timing, techniques, and tools needed for healthy growth.",
      tags: ["Pruning", "Trees", "Shrubs"]
    }
  ];

  const ticketCategories = [
    'General Inquiry',
    'Service Request',
    'Billing Question',
    'Technical Support',
    'Complaint',
    'Feedback',
    'Emergency Service'
  ];

  const priorities = ['Low', 'Normal', 'High', 'Urgent'];

  const handleInputChange = (field, value) => {
    setTicketForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    if (!ticketForm.customer_name || !ticketForm.customer_email || !ticketForm.subject || !ticketForm.message) {
      setError('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(ticketForm.customer_email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check CAPTCHA verification
    if (!captchaVerified) {
      setError('Please complete the security verification');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await fetch(createApiUrl('api/tickets/external/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...ticketForm
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setTicketForm({
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          subject: '',
          message: '',
          category: '',
          priority: 'Normal'
        });
        setCaptchaVerified(false);
        setCaptchaAnswer('');
        
        // Store ticket ID for display
        if (data.ticket && data.ticket.id) {
          setSubmittedTicketId(data.ticket.id);
          setSubmittedTicketToken(data.ticket.status_token || null);
        }
      } else {
        setError(data.error || 'Failed to submit ticket. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredArticles = helpArticles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="support-container">
      {/* Header */}
      <div className="support-header">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ 
                width: '56px', 
                height: '56px', 
                background: 'white', 
                borderRadius: '16px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                marginRight: '1.5rem',
                color: '#000062',
                fontSize: '1.75rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}>
                🌱
              </div>
              <div>
                <h1>Paramount Landscaping</h1>
                <h2>Customer Support Center</h2>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.2)', 
                padding: '0.75rem 1.5rem', 
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.25rem' }}>📞</span>
                <span style={{ fontSize: '1.125rem', fontWeight: '600' }}>(555) 123-4567</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        {/* Hero Section */}
        <div className="hero-section">
          <h1 className="hero-title">How Can We Help You?</h1>
          <p className="hero-subtitle">
            Get the support you need for all your landscaping services. Submit a ticket, browse our help articles, or contact us directly.
          </p>
          
          {/* Quick Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">⚡</div>
              <div className="stat-title">Fast Response</div>
              <div className="stat-description">Average response time: 2 hours</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔒</div>
              <div className="stat-title">Secure & Private</div>
              <div className="stat-description">Your information is protected</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💡</div>
              <div className="stat-title">Expert Support</div>
              <div className="stat-description">Professional landscaping experts</div>
            </div>
          </div>
        </div>

        <div className="main-content">
          {/* Submit Ticket Form */}
          <div className="form-card">
            <div className="form-header">
              <h2 className="form-title">
                <div className="form-icon">📝</div>
                Submit a Support Ticket
              </h2>
            </div>

            <div className="form-content">
              {submitSuccess && (
                <div className="alert alert-success">
                  <div style={{ marginBottom: '1rem' }}>
                    ✅ <strong>Ticket Submitted Successfully!</strong><br />
                    We've received your request and will respond within 2 hours during business hours.
                  </div>
                  {submittedTicketId && (
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.2)', 
                      padding: '1rem', 
                      borderRadius: '8px',
                      marginTop: '1rem'
                    }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                        Your Ticket ID: #{submittedTicketId}
                      </div>
                      <div style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
                        Save this number to track your ticket status
                      </div>
                      <a 
                        href={`/support/status?ticketId=${submittedTicketId}${submittedTicketToken ? `&t=${submittedTicketToken}` : ''}`}
                        style={{ 
                          background: 'white', 
                          color: '#10b981', 
                          padding: '0.5rem 1rem', 
                          borderRadius: '6px', 
                          textDecoration: 'none', 
                          fontWeight: '600',
                          display: 'inline-block'
                        }}
                      >
                        🔍 Track Ticket Status
                      </a>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="alert alert-error">
                  ❌ {error}
                </div>
              )}

              <form onSubmit={handleSubmitTicket}>
                {/* Contact Information Section */}
                <div className="form-section">
                  <div className="section-header">Contact Information</div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Your Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={ticketForm.customer_name}
                        onChange={(e) => handleInputChange('customer_name', e.target.value)}
                        required
                        disabled={submitting}
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Email Address <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-input"
                        value={ticketForm.customer_email}
                        onChange={(e) => handleInputChange('customer_email', e.target.value)}
                        required
                        disabled={submitting}
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={ticketForm.customer_phone}
                        onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                        disabled={submitting}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Category <span className="required">*</span>
                      </label>
                      <select
                        className="form-input form-select"
                        value={ticketForm.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        required
                        disabled={submitting}
                      >
                        <option value="">Select a category</option>
                        {ticketCategories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Ticket Details Section */}
                <div className="form-section">
                  <div className="section-header">Ticket Details</div>
                  
                  <div className="form-row form-row-single">
                    <div className="form-group">
                      <label className="form-label">
                        Subject <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={ticketForm.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        required
                        disabled={submitting}
                        placeholder="Brief description of your issue"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row form-row-single">
                    <div className="form-group">
                      <label className="form-label">
                        Describe your issue or question <span className="required">*</span>
                      </label>
                      <textarea
                        className="form-input form-textarea"
                        value={ticketForm.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        required
                        disabled={submitting}
                        placeholder="Please provide as much detail as possible to help us assist you better..."
                        rows="4"
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Priority</label>
                      <select
                        className="form-input form-select"
                        value={ticketForm.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        disabled={submitting}
                      >
                        {priorities.map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      {/* Empty space for alignment */}
                    </div>
                  </div>
                </div>

                {/* Security Section */}
                <div className="form-section">
                  <div className="section-header">Security Verification</div>
                  
                  <div className="captcha-container">
                    <div className="captcha-header">Please solve this math problem to verify you're human:</div>
                    <div className="captcha-problem">
                      <div className="captcha-math">7 + 3 = ?</div>
                      <button
                        type="button"
                        className="captcha-refresh"
                        onClick={() => {
                          const num1 = Math.floor(Math.random() * 10) + 1;
                          const num2 = Math.floor(Math.random() * 10) + 1;
                          const operation = Math.random() > 0.5 ? '+' : '-';
                          const answer = operation === '+' ? num1 + num2 : Math.max(num1, num2) - Math.min(num1, num2);
                          const question = operation === '+' ? `${num1} + ${num2}` : `${Math.max(num1, num2)} - ${Math.min(num1, num2)}`;
                          
                          // Update the display
                          const mathElement = document.querySelector('.captcha-math');
                          if (mathElement) {
                            mathElement.textContent = `${question} = ?`;
                          }
                          
                          // Store the answer and reset verification
                          setCaptchaAnswer(answer.toString());
                          setCaptchaVerified(false);
                        }}
                        disabled={submitting}
                      >
                        🔄 Refresh
                      </button>
                    </div>
                    <input
                      type="number"
                      className="form-input captcha-input"
                      placeholder="Enter your answer"
                      disabled={submitting}
                      value={captchaAnswer}
                      onChange={(e) => {
                        const userAnswer = e.target.value;
                        setCaptchaAnswer(userAnswer);
                        setCaptchaVerified(userAnswer === '10'); // Default answer for 7+3
                      }}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="submit-container">
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={submitting || !captchaVerified}
                  >
                    {submitting ? '⏳ Submitting...' : '📤 Submit Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Help Articles */}
          <div className="articles-card">
            <div className="articles-header">
              <h2 className="articles-title">
                <div className="form-icon">📚</div>
                Help Articles
              </h2>
            </div>

            <div className="articles-content">
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="articles-list">
                {filteredArticles.map((article) => (
                  <div key={article.id} className="article-item">
                    <div className="article-header">
                      <h3 className="article-title">{article.title}</h3>
                    </div>
                    <div className="article-meta">
                      <span className="article-category">{article.category}</span>
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="article-tag">{tag}</span>
                      ))}
                    </div>
                    <div className="article-content">
                      {article.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="contact-section">
          <h2 className="contact-title">Other Ways to Reach Us</h2>
          <div className="contact-grid">
            <div className="contact-item">
              <div className="contact-icon">📞</div>
              <div className="contact-label">Phone</div>
              <div className="contact-value">
                (555) 123-4567<br />
                Mon-Fri: 8AM-6PM
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">✉️</div>
              <div className="contact-label">Email</div>
              <div className="contact-value">
                support@paramountlandscaping.ca<br />
                We'll respond within 2 hours
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">📍</div>
              <div className="contact-label">Office</div>
              <div className="contact-value">
                123 Landscaping Way<br />
                City, Province A1A 1A1
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportLanding;